import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, Group, ScriptTestMode, IGridsterOptions, IDisplayedColumn, TreeType } from '../../../models';
import { GridsterOptions } from '../../gridsterOptions';
import { toggleEditMode, groupScriptModeChanged, guidEmpty } from '../../../common';
import { IScriptEditionService, IGroupService, IUserSettingsService } from '../../../services';
import { IRootScope } from '../../../IRootScope';
import { IScriptControl } from '../../../components';

interface IGroupScriptState {
    gridsterOptions: IGridsterOptions;
    isLoading: boolean;
    loadingMessage: string;
    groupGuid: string;
    selectedGroup: Group;
}

interface IViewClasses {
    script: string;
    console: string;
}

enum ConsoleViewMode {
    Compact,
    Medium,
    Full
}

const userSettingViewmode = 'script-state-console-viewmode';

class GroupScriptStateController implements IGroupScriptState, angular.IOnChanges {
    formController: angular.IFormController;
    gridsterOptions: IGridsterOptions;
    group: Group;
    groupGuid: string;
    identifiersMenu: IDisplayedColumn[];
    initialFilter: string;
    isCodeEditorDisabled: boolean;
    isLoading: boolean;
    isReadOnly: boolean;
    loadingMessage: string;
    selectedGroup: Group;
    scriptCtrl: IScriptControl;
    scriptTestMode: ScriptTestMode;
    viewMode: ConsoleViewMode = ConsoleViewMode.Compact;
    viewClasses: IViewClasses;
    withChildren: TreeType;

    static $inject = [
        '$scope',
        '$rootScope',
        '$timeout',
        'groupService',
        'scriptEditionService',
        'userSettingsService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$timeout: angular.ITimeoutService,
        private _groupService: IGroupService,
        private _scriptEditionService: IScriptEditionService,
        private _userSettingsService: IUserSettingsService
    ) {
    }

    $onInit() {
        const viewModeSetting = this._userSettingsService.getSettingValue(userSettingViewmode);
        this.viewMode = viewModeSetting === undefined || viewModeSetting === null
            ? ConsoleViewMode.Compact
            : +viewModeSetting; // use unary operator to convert to number
        this.gridsterOptions = new GridsterOptions().options();
        this.groupGuid = this.group.guid;
        this.isReadOnly = !this._$rootScope.isEditMode;
        this.selectedGroup = this.group;
        this.initialFilter = `g:${this.selectedGroup.uniqueName}`;
        this._groupService.getAllAsTree()
            .then(data => {
                this.withChildren = _.find(data, d => d.guid === guidEmpty);
            });
        this.identifiersMenu = [
            { isVisible: true, property: 'name', type: 'name', label: 'Name' },
            { isVisible: true, property: 'groupUniqueName', type: 'groupUniqueName', label: 'Group' },
            { isVisible: true, property: 'unitName', type: 'unit', label: 'Unit' },
        ];
        this._$scope.$on(groupScriptModeChanged, (event, data) => {
            this.scriptTestMode = data;
        });

        this._$scope.$on(toggleEditMode, (event, data) => {
            this.isReadOnly = !this._$rootScope.isEditMode;
        });
        this._$scope.$watch(() => this.formController, (newValue) => {
            if (newValue) {
                this._scriptEditionService.scriptFormController(this.formController);
            }
        });
        this.computeViewClasses();
    }
    
    $onChanges(onChangesObj: angular.IOnChangesObject): void {
        if (onChangesObj['group']) {
            this.selectedGroup = this.group;
        }
    }

    expand() {
        if (this.viewMode < ConsoleViewMode.Full) {
            this.viewMode++;
            this.saveViewMode();
        }
        this.computeViewClasses();
    }

    reduce() {
        if (this.viewMode > ConsoleViewMode.Compact) {
            this.viewMode--;
            this.saveViewMode();
        }
        this.computeViewClasses();
    }

    private saveViewMode() {
        this._userSettingsService.setSettingValue(userSettingViewmode, this.viewMode.toString());
    }

    private computeViewClasses() {
        switch (this.viewMode) {
            case ConsoleViewMode.Medium:
                this.viewClasses = {
                    script: 'flex-row-lg-6',
                    console: 'flex-row-lg-6'
                };
                break;
            case ConsoleViewMode.Full:
                this.viewClasses = {
                    script: '',
                    console: 'flex-row-lg-12'
                };
                break;
            case ConsoleViewMode.Compact:
            default:
                this.viewClasses = {
                    script: 'flex-row-lg-8',
                    console: 'flex-row-lg-4'
                };
                break;
        }
        this._$timeout(() => this.scriptCtrl.refresh());
    }
}

export const wtGroupScriptState: ComponentDefinition = {
    componentId: 'wtGroupScriptState',
    controller: GroupScriptStateController,
    template: require('./groupScriptState.component.html'),
    controllerAs: 'vm',
    bindings: {
        formController: '=?',
        group: '<',
        isCodeEditorDisabled: '<'
    }
};